<template>
  <div :class="$style.page">
    <BaseContainer>
      <BaseLoader
        :is-active="!isDataInitialized"
        is-block
      >
        <template v-if="!hasSelectedBrand">
          <ProductsBrandGrid>
            <ProductsBrandLogoCard
              v-for="brand in oemBrands"
              :key="brand.name"
              :brand="brand"
              @click="selectBrand(brand.name)"
            />
          </ProductsBrandGrid>
        </template>

        <template v-else>
          <teleport
            v-if="isDataInitialized"
            to="#headerPageContent"
          >
            <HeaderBarItem>
              <ProductsSwitchViewButton
                v-model="viewMode"
              />
            </HeaderBarItem>
            <HeaderBarItem>
              <HeaderSearch
                placeholder="Search cars…"
                @change="setSearchTerm"
              />
            </HeaderBarItem>
            <HeaderBarItem>
              <CarsFilterDropdown
                v-model="filterModel"
                :schema="filterSchema"
                :schema-options="filterSchemaOptions"
                :reset-all="resetAll"
                :apply-filters="applyFilters"
              />
            </HeaderBarItem>
          </teleport>

          <template v-if="viewMode === 'list'">
            <BaseContainer :class="$style.page">
              <TableSection
                :page="page"
                :data="cars"
                :total-items="totalCars"
                :per-page="perPage"
                @update:page="setPage"
              >
                <template #heading>
                  All cars
                </template>

                <CarsTableColumns />
              </TableSection>
            </BaseContainer>
          </template>
          <template v-else-if="viewMode === 'grid'">
            <ProductsCardsGrid has-borders>
              <ProductsCarCard
                v-for="car in cars"
                :key="car.id"
                :car-id="car.id"
                :model="car.model"
                :brand-logo="car.brand.logo"
                :brand-name="car.brand.name"
                :project-code="car.project_code"
                :power-train="car.power_train"
                :body="car.body"
                :generation="car.generation"
                :phase="car.phase"
                :front-image="car.front_image"
                :back-image="car.back_image"
                :sop="car.volumes_infos.sop"
                :eop="car.volumes_infos.eop"
                :country="car.country"
              />
            </ProductsCardsGrid>
            <BaseContainer :class="$style.pagination">
              <BasePagination
                v-if="totalCars > 0"
                :total-items="totalCars"
                :current-page="page"
                :per-page="perPage"
                @setPage="setPage"
              />
            </BaseContainer>
          </template>
        </template>
      </BaseLoader>
    </BaseContainer>
  </div>
</template>

<script>
import { difference } from 'lodash';
import { mapState } from 'vuex';
import BaseContainer from '@/components/base/Container';
import BasePagination from '@/components/base/Pagination';
import BaseLoader from '@/components/base/Loader';
import TableSection from '@/components/TableSection';
import CarsTableColumns from '@/components/CarsTableColumns';
import HeaderBarItem from '@/components/HeaderBarItem';
import HeaderSearch from '@/components/HeaderSearch';
import CarsFilterDropdown from '@/components/CarsFilterDropdown';
import ProductsSwitchViewButton from '@/components/ProductsSwitchViewButton';
import ProductsCardsGrid from '@/components/ProductsCardsGrid';
import ProductsCarCard from '@/components/ProductsCarCard';
import carsFilter from '@/products/carsFilter';
import ProductsBrandLogoCard from '@/components/ProductsBrandLogoCard';
import ProductsBrandGrid from '@/components/ProductsBrandGrid';
import brands from '@/brands';

export default {
  components: {
    BaseContainer,
    BasePagination,
    BaseLoader,
    TableSection,
    CarsTableColumns,
    HeaderBarItem,
    HeaderSearch,
    CarsFilterDropdown,
    ProductsSwitchViewButton,
    ProductsCardsGrid,
    ProductsCarCard,
    ProductsBrandLogoCard,
    ProductsBrandGrid,
  },
  data() {
    return {
      cars: [],
      totalCars: 0,
      perPage: 12,
      page: 1,
      viewMode: 'grid',
      searchTerm: '',
      filterModel: null,
      filterSchema: null,
      filterSchemaOptions: null,
      isDataInitialized: false,
      lastCallId: 0,
      oemBrands: [],
      hasSelectedBrand: false,
    };
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapState('app', ['carsId']),
  },
  watch: {
    filterModel: {
      deep: true,
      handler: function modelFilterChanged(prevValue, newValue) {
        const changedField = { field: null, direction: null };
        if (newValue) {
          Object.keys(newValue).filter((item) => {
            if (difference(prevValue[item], newValue[item]).length !== 0) {
              changedField.field = item;
              changedField.direction = 'add';
            }

            if (difference(newValue[item], prevValue[item]).length !== 0) {
              changedField.field = item;
              changedField.direction = 'remove';
            }

            return true;
          });
        }
        if (changedField.field) {
          this.loadDynamicSchemaOptions(changedField);
        }
      },
    },
  },
  async created() {
    Promise.all([brands.getCarsBrands(), carsFilter.getOptions()])
      .then(async (results) => {
        [this.oemBrands, this.filterSchemaOptions] = results;
        this.filterSchema = carsFilter.getSchema();
        this.filterModel = carsFilter.getModel();
        await this.loadPage();
        this.isDataInitialized = true;
      });
  },
  methods: {
    async selectBrand(brand) {
      this.filterModel.oemBrand = [brand];
      await this.loadPage();
      window.scrollTo(0, 0);
      this.hasSelectedBrand = true;
    },
    setPage(page) {
      this.page = page;
      this.loadPage();
    },
    setSearchTerm(term) {
      this.searchTerm = term;
      this.setPage(1);
    },
    async loadDynamicSchemaOptions(changedField) {
      if (this.isDataInitialized) {
        this.filterSchemaOptions = {
          ...this.filterSchemaOptions,
          ...(await carsFilter.getDynamicOptions(this.filterModel, this.searchTerm, changedField, this.carsId)),
        };
      }
    },
    async loadPage() {
      this.isDataInitialized = false;
      this.lastCallId += 1;
      const callId = this.lastCallId;

      const res = await carsFilter.getData(
        this.filterModel,
        this.searchTerm,
        this.perPage,
        this.page,
        this.user.company.uuid,
        this.carsId,
      );

      if (this.lastCallId !== callId) return;

      this.cars = res.data;
      this.totalCars = res.total;
      this.isDataInitialized = true;
    },
    async resetAll() {
      this.filterSchemaOptions = await carsFilter.getOptions();
      this.filterModel = carsFilter.getModel();
      this.setPage(1);
    },
    applyFilters() {
      this.setPage(1);
    },
  },
};
</script>

<style lang="scss" module>
.page {
  flex-grow: 1;
  padding-top: map-get($page-vertical-paddings, 'small');
  padding-bottom: map-get($page-vertical-paddings, 'small');
}

.pagination {
  margin-top: 15px;
  margin-bottom: 15px;
}
</style>
